import { Component, Injector, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { TemplateMessageObject, TemplateMessageObjectPager } from '@trendbuild/trend-cloud-api';
import { AbstractComponent, Channel, ChannelService, ChannelTypeEnum, Pager, WaTemplateResponse } from 'lib-trend-core';
import { debounceTime, Observable, takeUntil } from 'rxjs';

export enum ConversationCategoryEnum {
  AUTHENTICATION = 'Authentication',
  MARKETING = 'Marketing',
  SERVICE = 'Service',
  UTILITY = 'Utility',
}

@Component({
    selector: 'app-report-templates-analytics',
    templateUrl: './report-templates-analytics.v2.component.html',
    // templateUrl: './report-templates-analytics.component.html',
    styleUrl: './report-templates-analytics.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class ReportTemplatesAnalyticsComponent extends AbstractComponent implements OnInit {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;
  searchControl: FormControl = new FormControl('');

  allTemplatesRawList: TemplateMessageObject[] = [];
  filteredTemplates: TemplateMessageObject[] = [];

  templates: Partial<WaTemplateResponse>[] = [];
  pager: Pager<Partial<WaTemplateResponse>> = new Pager<Partial<WaTemplateResponse>>({ perPage: 19 });

  private loadSeq: number = 0;
  listObservable: Observable<Pager<Partial<WaTemplateResponse>>>;
  previewArray: Array<{ text: string; type: string, example?: object }>

  defaultChannel: Channel;

  listChannel: Array<Channel> = [];
  labelChannel = 'report.templatesAnalytics.channelsFilter';

  listCategories: { key: string; label: string }[] = [
    { key: ConversationCategoryEnum.AUTHENTICATION, label: 'report.category.authentication' },
    { key: ConversationCategoryEnum.MARKETING, label: 'report.category.marketing' },
    { key: ConversationCategoryEnum.SERVICE, label: 'report.category.service' },
    { key: ConversationCategoryEnum.UTILITY, label: 'report.category.utility' },
  ];

  selectedCategory: string = '';
  selectedChannel: Channel;


  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcTemplatesAnalytics' },
  ];

  constructor(
    injector: Injector,
    private channelService: ChannelService,
  ) {
    super(injector);
    this.setupSearchListener();
  }

  ngOnInit(): void {
    this.pager = {
      page: 1,
      perPage: 10,
      previousPage: 0,
      total: 0,
      list: []
    };

    this.setupForms();

    this.channelService.getList().subscribe({
      next: (channels: Channel[]) => {
        this.listChannel = channels.filter((c: Channel) => c.type === ChannelTypeEnum.CLOUD_API);

        if (this.listChannel.length > 0) {
          this.defaultChannel = this.listChannel[0];
          this.labelChannel = this.defaultChannel.name;

          this.formGroup.get('channel')?.setValue(this.defaultChannel);

          this.getTemplatesByChannel();
        }
      },
      error: (err) => console.error('Erro ao carregar canais:', err)
    });
  }

  private setupForms(): void {
    this.formGroup = this.formBuilder.group({
      channel: [null],
      category: [null],
    });

    let previousValue = this.formGroup.value;

    this.formGroup.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(currentValue => {
        if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
          previousValue = currentValue;

          this.pager.page = 1;
          this.applyFilters();
        }
      });
  }

  private setupSearchListener() {
    this.searchControl.valueChanges.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.pager.page = 1;
        this.applyFilters(value);
      });
  }

  private getTemplatesByChannel(): void {
    this.loadingContent = true;
    const channelId = this.formGroup.get('channel')?.value?._id
    if (!channelId) {
      this.allTemplatesRawList = [];
      this.filteredTemplates = [];
      this.templates = [];
      this.pager.total = 0;
      // No request is made on this path, so the skeleton raised above has to come down here — and
      // the load token is retired so an answer still in flight cannot clear it later instead.
      this.loadSeq++;
      this.loadingContent = false;
      return;
    }

    // A listing already read once is replayed synchronously, one still being fetched is not, so a
    // load started later can land first and be overwritten by the earlier one — which would report
    // one channel's templates under another channel's name.
    const seq = ++this.loadSeq;
    const isCurrentLoad = () => seq === this.loadSeq;

    this.channelService.getTemplatesByChannel(channelId).pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates: TemplateMessageObjectPager) => {
          if (!isCurrentLoad()) {
            return;
          }
          this.allTemplatesRawList = templates.data ?? [];

          this.pager.page = 1;
          this.filteredTemplates = [];

          this.applyFilters();
          this.loadingContent = false;
        },
        complete: () => {
          if (isCurrentLoad()) {
            this.loadingContent = false;
          }
        },
        error: (err) => {
          console.error('Error fetching templates:', err);
          if (isCurrentLoad()) {
            this.loadingContent = false;
          }
        }
      });
  }

  private applyFilters(searchValue: string = (this.searchControl.value ?? '')): void {
    let listTemplates = [...this.allTemplatesRawList];

    const category = (String(this.selectedCategory) ?? '').toLowerCase();
    if (category) {
      listTemplates = listTemplates.filter(t => String(t.category ?? '').toLowerCase() === category);
      this.pager.page = 1;
      this.selectedCategory = category;
    }

    const search = searchValue.toLowerCase().trim();
    search &&
      (listTemplates = listTemplates.filter(t => {
        return t.name.toLowerCase().includes(search) || t.category?.toLowerCase().includes(search)
      }));

    this.filteredTemplates = listTemplates;
    this.pager.total = listTemplates.length;

    const maxPage = Math.max(1, Math.ceil(this.pager.total / this.pager.perPage));
    if (this.pager.page > maxPage) this.pager.page = 1;
    this.sliceToTemplates();
  }

  loadPage(page: number) {
    this.pager.page = page;
    this.sliceToTemplates();
  }

  searchByChannel(channel: Channel | null): void {
    this.labelChannel = channel?.name ?? 'report.templatesAnalytics.channelsFilter';
    this.formGroup.get('channel')?.setValue(channel);
    this.getTemplatesByChannel();
  }

  dictionaryLanguage(language: string) {
    const dictionary = {
      "pt_BR": this.t('report.templatesAnalytics.langPortuguese'),
      "pt_PT": this.t('report.templatesAnalytics.langPortuguese'),
      "en_US": this.t('report.templatesAnalytics.langEnglish'),
      "en": this.t('report.templatesAnalytics.langEnglish'),
    }

    return dictionary[language]
  }

  dictionaryStatus(status: string) {
    const dictionary = {
      "APPROVED": this.t('report.templatesAnalytics.statusApproved'),
    }

    return dictionary[status]
  }

  dictionaryCategory(category: string) {
    const dictionary = {
      "AUTHENTICATION": this.t('report.category.authentication'),
      "MARKETING": this.t('report.category.marketing'),
      "SERVICE": this.t('report.category.service'),
      "UTILITY": this.t('report.category.utility')
    }

    return dictionary[category]
  }

  openTemplateDetail(template: any): void {
    this.router.navigate([`/report/${template.id}/detail`], {
      queryParams: {
        id: template.id,
        name: template.name,
        category: template.category,
        channel: this.defaultChannel._id,
      }
    });
  }

  filterByCategory(category: string) {
    this.selectedCategory = category;
    this.pager.page = 1;
    this.applyFilters();
  }

  filterTemplates(event: any) {
    const value = typeof event === 'string' ? event : (event?.target?.value ?? '');
    this.pager.page = 1;
    this.applyFilters(value);
  }

  clearFilters(): void {
    this.selectedCategory = '';
    this.searchControl.setValue('', { emitEvent: false });
    this.pager.page = 1;
    this.applyFilters();
  }

  private sliceToTemplates(): void {
    const startIdx = (this.pager.page - 1) * this.pager.perPage;
    const endIdx = startIdx + this.pager.perPage;
    this.templates = this.filteredTemplates.slice(startIdx, endIdx);
  }

}

